import { attempt, isDefined } from "@banjoanton/utils";
import { Chess } from "chess.js";
import { BoardQuery } from "../../../../utils/board-query";
import { EvaluationComparerUtil } from "../../../../utils/evaluation-comparer-util";
import { ForkUtil } from "../../../../utils/fork-util";
import type { MoveAnnotation } from "../../models/move-annotation";
import type { CalculateAnnotationFunction } from "../annotation-service";

const EVAL_DEPTH = 10;

const calculate: CalculateAnnotationFunction = async ({
    move,
    previousMoves,
    evaluator,
}) => {
    const positionAfterMove = BoardQuery.fromMove(move);
    const colorThatMoved = positionAfterMove.lastMoved;
    const nextMoveColor = positionAfterMove.turn;
    const ourPieces = positionAfterMove.pieces(colorThatMoved);

    const wasCheckBeforeMove = new Chess(move.fenBefore).inCheck();
    if (wasCheckBeforeMove) {
        // do not consider undefended pieces when moving out of check
        return [];
    }

    // Mutable engine for move simulation
    const engine = new Chess();

    // if previous move was a fork, skip hanging piece detection for the squares involved in the fork
    const forkedSquares = ForkUtil.getSquaresAttackedByFork(previousMoves);

    const hangingPiecesAnnotations: MoveAnnotation[] = [];

    for (const piece of ourPieces) {
        engine.load(positionAfterMove.fen);
        const positionQuery = BoardQuery.from(engine);
        const square = piece.square;

        // do not consider squares involved in forks
        if (forkedSquares.includes(square)) {
            continue;
        }

        const attackers = positionQuery
            .attackers(square)
            // remove attackers that cannot actually move to the square (e.g. pinned pieces)
            .filter((attacker) => {
                engine.load(positionAfterMove.fen);
                const successfulMove = attempt(() =>
                    engine.move({ from: attacker.square, to: square })
                );

                if (!successfulMove) {
                    return false;
                }

                return true;
            });

        if (attackers.length === 0) {
            continue;
        }

        const defenders = positionQuery.defenders(square);

        // check for x-ray defenders (an attacker between two defenders)
        let hasXRayDefender = false;
        for (const attacker of attackers) {
            engine.load(positionAfterMove.fen);
            engine.remove(attacker.square);
            // cannot use BoardQuery here because the king might be the removed piece, which is not valid
            const newDefenders = engine.attackers(square, colorThatMoved);
            if (newDefenders.length > 0) {
                hasXRayDefender = true;
            }
        }
        engine.load(positionAfterMove.fen);

        if (defenders.length > 0 || hasXRayDefender) {
            continue;
        }

        // Ignore all capture moves for now, use another annotation for bad trades or similar
        const isCaptureMove = move.captured !== undefined;
        if (isCaptureMove) {
            continue;
        }

        // try doing the attacking move (catching the hanging piece) and see if the score improves for the attacker
        // if the score improves for the attacking side, then it's really hanging
        const evalBeforeHangingPiece = await evaluator.evaluatePosition(
            move.fenBefore,
            EVAL_DEPTH
        );

        let captureIsGoodForOpponent = false;
        for (const attacker of attackers) {
            engine.load(positionAfterMove.fen);

            const successfulMove = attempt(() =>
                engine.move({ from: attacker.square, to: square })
            );

            if (!successfulMove) {
                continue;
            }

            const evalAfterAttack = await evaluator.evaluatePosition(
                engine.fen(),
                EVAL_DEPTH
            );

            // after opponent captures the hanging piece, their evaluation should be better or equal, meaning the capture was good for them
            const comparison = EvaluationComparerUtil.compareScore({
                oldScore: evalBeforeHangingPiece.evaluationMessage.score,
                newScore: evalAfterAttack.evaluationMessage.score,
                side: nextMoveColor,
                threshold: 50,
            });

            if (
                comparison.result === "better" ||
                comparison.result === "equal"
            ) {
                captureIsGoodForOpponent = true;
            }
        }

        if (!captureIsGoodForOpponent) {
            continue;
        }

        const wasUndefendedBefore = attempt(() => {
            if (!isDefined(previousMoves)) {
                return false;
            }
            const previousMoveForSamePlayer =
                previousMoves[previousMoves.length - 2];

            const previousAnnotations =
                previousMoveForSamePlayer?.analysis?.annotations;
            if (!isDefined(previousAnnotations)) {
                return false;
            }

            return previousAnnotations.some(
                (a) =>
                    a.type === "undefended-piece" &&
                    a.piece === piece.type &&
                    a.square === square
            );
        });

        hangingPiecesAnnotations.push({
            type: "undefended-piece",
            piece: piece.type,
            square,
            isFirstTime: !wasUndefendedBefore,
        });
    }

    return hangingPiecesAnnotations;
};

export const UndefendedPieceAnnotationService = {
    calculate,
};
